import { sendBanEmail, unbanUser } from '@gozoki/api'
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useToast } from '~/components/communication/Toast'
import { USERS_TAB_PERMISSIONS } from '~/components/routes/users/tabs'
import { useDeleteUser } from '~/components/routes/users/use-delete-selection'
import { paymentDefaultUsersColumns } from '~/components/routes/users/users-columns'
import { UsersHeaderActions } from '~/components/routes/users/users-header-actions'
import {
  PAGE_SIZE_OPTIONS,
  UsersPageLayout,
  UsersSearchGrid,
} from '~/components/routes/users/users-page-layout'
import { useDebounce } from '~/hooks/use-debounce'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { getErrorMessageWithSentry } from '~/utils/sentry'

export const Route = createFileRoute('/_authenticated/users/payment-defaults')({
  staticData: {
    permission: USERS_TAB_PERMISSIONS['payment-defaults'],
  },
  component: PaymentDefaultUsersPage,
})
withRoutePermissions(Route)

const SEARCH_DEBOUNCE_MS = 500

function PaymentDefaultUsersPage() {
  const [Toast, showToast] = useToast()
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [selected, setSelected] = useState<number[]>([])

  const debouncedSearch = useDebounce(search.trim(), SEARCH_DEBOUNCE_MS)

  const { data, refetch, isFetching } = useQuery(
    api.users.paymentDefaultUsers.queryOptions(
      {
        query: {
          page: page + 1,
          pageSize,
          search: debouncedSearch || undefined,
        },
      },
      { placeholderData: keepPreviousData },
    ),
  )

  const handleDelete = useDeleteUser(selected, showToast, () => {
    setSelected([])
    return refetch()
  })

  const sendEmailMut = useMutation({
    mutationFn: (id: number) => sendBanEmail(id),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({ message: 'Email envoyé', severity: 'success' })
    },
  })

  const unbanMut = useMutation({
    mutationFn: (id: number) => unbanUser(id),
    onError: (error) => {
      showToast({ message: getErrorMessageWithSentry(error), severity: 'error' })
    },
    onSuccess: () => {
      showToast({ message: 'Utilisateur débanni', severity: 'success' })
      refetch()
    },
  })

  return (
    <UsersPageLayout
      activeTab="payment-defaults"
      actions={<UsersHeaderActions selected={selected} handleDelete={handleDelete} />}
    >
      <Toast />
      <UsersSearchGrid
        search={search}
        onSearchChange={(value) => {
          setSearch(value)
          setPage(0)
        }}
        columns={paymentDefaultUsersColumns(sendEmailMut.mutateAsync, unbanMut.mutateAsync)}
        rows={data?.items ?? []}
        loading={isFetching}
        onRowSelectionModelChange={setSelected}
        serverPagination={{
          rowCount: data?.total ?? 0,
          page,
          pageSize,
          onChange: (model) => {
            setPage(model.page)
            setPageSize(model.pageSize)
          },
        }}
      />
    </UsersPageLayout>
  )
}
